import {
    SaveThirdPartyPayoffRowResponse,
    ThirdPartyPayoff,
    VendorContractCell,
} from '../../../models/FlowOfFunds/flow-of-funds.model';

/** Row-level write responses come from the FoF module's contract factory,
 *  which leaves the composition-owned fields — `cells`, `isAssumedByBuyer`,
 *  `payoffAmount` — at their defaults; only the full GET fills them. Replacing
 *  the whole row with a write response would therefore wipe the DAQI cells and
 *  LOI values from the table. Merge instead: FoF-owned fields (payoffDate,
 *  perDiem, wireInstructions, the two document slots, approvals, removalProposal)
 *  and the fresh `capabilities` (D16/R3) come from the response; the
 *  composition-owned fields stay from the row already in state. */
export const mergeThirdPartyPayoffWriteResponse = (
    existing: ThirdPartyPayoff | undefined,
    response: ThirdPartyPayoff
): ThirdPartyPayoff => {
    if (!existing) {
        return response;
    }
    return {
        ...response,
        cells: existing.cells,
        contractDocuments: existing.contractDocuments,
        isAssumedByBuyer: existing.isAssumedByBuyer,
        payoffAmount: existing.payoffAmount,
    };
};

/** The save response carries ONLY the cells that were in the request, each with
 *  its post-save version. Patch those into the row by `customColumnId` and leave
 *  every other cell untouched — replacing the array wholesale would drop the
 *  cells the user did not edit in this save and blank those DAQI columns.
 *  Unedited cells keep valid versions: DAQI bumps a cell's version only when its
 *  answer actually changes, so there is nothing to refresh for them. */
export const patchThirdPartyPayoffCells = (
    cells: VendorContractCell[],
    savedCells: VendorContractCell[]
): VendorContractCell[] => {
    const patched = [...cells];
    savedCells.forEach((savedCell) => {
        const index = patched.findIndex(
            (cell) => cell.customColumnId === savedCell.customColumnId
        );
        if (index >= 0) {
            patched[index] = savedCell;
        } else {
            patched.push(savedCell);
        }
    });
    return patched;
};

export const mergeThirdPartyPayoffRowSaveResponse = (
    existing: ThirdPartyPayoff | undefined,
    response: SaveThirdPartyPayoffRowResponse
): ThirdPartyPayoff => {
    const merged = mergeThirdPartyPayoffWriteResponse(existing, response.row);
    return {
        ...merged,
        cells: patchThirdPartyPayoffCells(merged.cells ?? [], response.cells),
    };
};
